<template>
  <div class="app flex-row align-items-center">
    <div class="container">
      <b-row class="justify-content-center">
        <b-col md="8">
          <!-- Logo -->
          <div class="text-center mb-4">
            <img src="/img/logo.png" alt="Logo" class="auth-logo">
          </div>

          <b-card-group>
            <b-card no-body class="p-4">
              <b-card-body>

                <!-- ── State: email ── -->
                <div v-if="loginState === 'email'">
                  <h1>{{ $t('login') }}</h1>
                  <p class="text-muted">{{ $t('magicLink.enterEmailDescription') }}</p>

                  <b-alert :show="!!errorMessage" variant="danger" class="mb-3">
                    {{ errorMessage }}
                  </b-alert>

                  <b-form @submit.prevent="sendMagicLink">
                    <b-input-group class="mb-3">
                      <b-input-group-prepend>
                        <b-input-group-text><i class="icon-envelope"></i></b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        type="email"
                        class="form-control"
                        :placeholder="$t('email')"
                        autocomplete="email"
                        v-model="username"
                        required
                      />
                    </b-input-group>

                    <b-row class="align-items-center">
                      <b-col cols="7">
                        <b-button
                          type="submit"
                          variant="primary"
                          class="px-4 bg-protokol-royal-purple protokol-royal-purple"
                          :disabled="sending"
                        >
                          {{ sending ? $t('magicLink.sending') : $t('magicLink.sendMagicLink') }}
                        </b-button>
                      </b-col>
                      <b-col cols="5" class="text-right">
                        <b-button
                          variant="link"
                          class="px-0 clr-protokol-royal-purple"
                          @click.prevent="switchToPassword"
                        >
                          {{ $t('magicLink.usePassword') }}
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-form>
                </div>

                <!-- ── State: magic_link_sent ── -->
                <div v-if="loginState === 'magic_link_sent'" class="text-center py-3">
                  <div class="magic-link-icon mb-3">&#x1F4E7;</div>
                  <h2>{{ $t('magicLink.checkInbox') }}</h2>
                  <p class="text-muted mt-2 mb-4">
                    {{ $t('magicLink.checkInboxDescription', { email: username }) }}
                  </p>
                  <b-button
                    variant="link"
                    class="clr-protokol-royal-purple d-block mb-2"
                    :disabled="sending"
                    @click.prevent="sendMagicLink"
                  >
                    {{ sending ? $t('magicLink.sending') : $t('magicLink.resendMagicLink') }}
                  </b-button>
                  <b-button
                    variant="link"
                    class="text-muted d-block"
                    @click.prevent="switchToPassword"
                  >
                    {{ $t('magicLink.usePassword') }}
                  </b-button>
                </div>

                <!-- ── State: password ── -->
                <div v-if="loginState === 'password'">
                  <h1>{{ $t('login') }}</h1>
                  <p class="text-muted">{{ $t('signIn') }}</p>

                  <b-alert :show="!!errorMessage" variant="danger" class="mb-3">
                    {{ errorMessage }}
                    <div v-if="showResend" class="mt-2">
                      <b-button size="sm" variant="outline-danger" @click.prevent="resendConfirmation">
                        {{ $t('resendConfirmationEmail') }}
                      </b-button>
                    </div>
                  </b-alert>
                  <b-alert :show="!!infoMessage" variant="success" class="mb-3">{{ infoMessage }}</b-alert>

                  <b-form @submit.prevent="login">
                    <b-input-group class="mb-3">
                      <b-input-group-prepend>
                        <b-input-group-text><i class="icon-envelope"></i></b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        type="text"
                        class="form-control"
                        :placeholder="$t('email')"
                        autocomplete="email"
                        v-model="username"
                      />
                    </b-input-group>
                    <b-input-group class="mb-4">
                      <b-input-group-prepend>
                        <b-input-group-text><i class="icon-lock"></i></b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        type="password"
                        class="form-control"
                        :placeholder="$t('password')"
                        autocomplete="current-password"
                        v-model="password"
                      />
                    </b-input-group>

                    <b-row class="align-items-center mb-3">
                      <b-col cols="6">
                        <b-button type="submit" variant="primary" class="px-4 bg-protokol-royal-purple protokol-royal-purple">
                          {{ $t('login') }}
                        </b-button>
                      </b-col>
                      <b-col cols="6" class="text-right">
                        <b-button variant="link-" class="px-0 clr-protokol-royal-purple" :to="{path: '/user/forgotpassword'}">
                          {{ $t('forgotPassword') }}?
                        </b-button>
                      </b-col>
                    </b-row>

                    <div class="text-center">
                      <b-button
                        variant="link"
                        class="clr-protokol-royal-purple px-0"
                        @click.prevent="switchToMagicLink"
                      >
                        {{ $t('magicLink.backToMagicLink') }}
                      </b-button>
                    </div>
                  </b-form>
                </div>

              </b-card-body>
            </b-card>

            <b-card no-body class="text-white bg-protokol-royal-purple py-5" style="width:100%" v-if="$env.get('CLUSTER_MODE') == 'shared'">
              <b-card-body class="text-center bg-protokol-royal-purple">
                <div>
                  <h2>{{ $t('signUp') }}</h2>
                  <p>{{ $t('signUpNow') }}</p>
                  <router-link to="/register">
                    <b-button class="mt-3 gradient-register-button">{{ $t('registerNow') }}!</b-button>
                  </router-link>
                </div>
              </b-card-body>
            </b-card>
          </b-card-group>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<style scoped>
.auth-logo {
  max-width: 60px;
  height: auto;
  margin-bottom: 1rem;
}

.card .card-body {
  border: none
}

.magic-link-icon {
  font-size: 48px;
  line-height: 1;
}

.gradient-register-button {
  background: linear-gradient(135deg, #2b2d31 0%, #1e1f22 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  color: white;
  font-weight: 600;
  padding: 0.5rem 2rem;
  transition: all 0.3s ease;
}

.gradient-register-button:hover {
  background: linear-gradient(135deg, #1e1f22 0%, #2b2d31 100%);
  border-color: rgba(102, 126, 234, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  color: white;
}

.gradient-register-button:active {
  transform: translateY(0);
}
</style>

<script>
import Axios from 'axios';

export default {
  name: 'Login',

  data: function() {
    return {
      loginState: 'email',
      username: '',
      password: '',
      errorMessage: '',
      infoMessage: '',
      showResend: false,
      sending: false
    }
  },

  methods: {
    clientFactory: function() {
      var http = Axios.create({
        baseURL: this.$env.get('API_HOST'),
        timeout: 10000,
        headers: {
          'X-Project-Uuid': localStorage.getItem('current_project_uuid')
        }
      });
      http.defaults.withCredentials = true;
      return http;
    },

    switchToPassword: function() {
      this.errorMessage = '';
      this.infoMessage = '';
      this.loginState = 'password';
    },

    switchToMagicLink: function() {
      this.errorMessage = '';
      this.infoMessage = '';
      this.loginState = 'email';
    },

    sendMagicLink: function() {
      var self = this;
      self.errorMessage = '';
      self.sending = true;

      self.clientFactory().post('/v1/user/magic-link', { email: self.username })
        .then(function() {
          self.loginState = 'magic_link_sent';
        })
        .catch(function(error) {
          if (error.response === undefined) {
            self.errorMessage = self.$t('lostConnection');
            return;
          }
          self.errorMessage = self.$t('lostConnection');
        })
        .finally(function() {
          self.sending = false;
        });
    },

    resendConfirmation: function() {
      var self = this;
      self.$http.post('/v1/user/send/confirmation/email', { email: self.username })
        .then(function() {
          self.showResend = false;
          self.errorMessage = '';
          self.infoMessage = self.$t('emailSent');
        })
        .catch(function(error) {
          if (error.response && error.response.status == 500) {
            self.$serverErrorModal(error);
          }
        });
    },

    login: function() {
      var self = this;
      var popup = self.$loadingPopup();

      self.clientFactory().post('/v1/user/login', {
        username: self.username,
        password: self.password
      })
      .then(function(resp) {
        self.$gtag.event('login', { user: resp.data });
        self.$store.commit('setUser', resp.data);

        if (self.$route.query['_ref'] !== undefined) {
          window.location.href = self.$route.query['_ref'];
          return;
        }

        self.$router.push('/');
      })
      .catch(function(err) {
        if (err.response === undefined) {
          self.errorMessage = self.$t('lostConnection');
          return;
        }

        if (err.response.status == 409) {
          self.errorMessage = self.$t('confirmEmail');
          self.showResend = true;
          return;
        }

        if (err.response.status == 308) {
          self.$router.push('/two-factor-auth');
          return;
        }

        self.errorMessage = err.response.data.message;
      })
      .finally(function() {
        popup.hide();
      });
    }
  },

  mounted: function() {
    var self = this;

    if (self.$route.query.email) {
      self.username = self.$route.query.email;
    }

    if (self.$route.query.method === 'password') {
      self.loginState = 'password';
    }

    if (self.$route.query.error === 'link_expired') {
      self.errorMessage = self.$t('magicLink.linkExpiredError');
    }

    self.clientFactory().get('/v1/user/claims')
      .then(function() {
        self.$router.push({ path: '/?force=true' });
      })
      .catch(function(error) {
        if (error.response === undefined) {
          throw error;
        }
        if (error.response.status == 500) {
          self.$serverErrorModal(error);
          return;
        }
        if (error.response.status != 401) {
          self.errorMessage = error.response.data.message;
        }
      });
  }
}
</script>

